import PropTypes from "prop-types";
import { useLoader } from "@/hooks/LoaderProvider";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "@/components/common/Button";
import { CheckOutlined, ArrowRightOutlined } from "@/config/antd";
import Avatar from "@/components/common/Avatar";
import Copyright from "@/components/layout/Footer/Copyright";
import { getFirstName } from "@/utils/nameUtils";
import Logo from "@/components/common/Logo";
import WebIcon from "@/components/common/Web_Icons";
import { selectUserInfo } from "@/store/redux/slices/auth/auth_slice";
import { useState } from "react";

const Subscription = ({
  showTopSection = true,
  showFooter = true,
  onProceed,
  selectedPlan: externalSelectedPlan,
  setSelectedPlan: externalSetSelectedPlan,
}) => {
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const userInfo = useSelector(selectUserInfo);
  const userFirstName = getFirstName(userInfo.fullname);

  // Internal state for standalone usage
  const [internalSelectedPlan, setInternalSelectedPlan] = useState(null);

  // Use external state if provided, otherwise use internal state
  const selectedPlan =
    externalSelectedPlan !== undefined
      ? externalSelectedPlan
      : internalSelectedPlan;
  const setSelectedPlan = externalSetSelectedPlan || setInternalSelectedPlan;

  const benefits = [
    "Unlimited transactions",
    "Advanced analytics",
    "Priority customer support",
    "Custom integrations",
    "Dedicated account manager",
  ];

  const handleProceed = () => {
    if (selectedPlan) {
      showLoader();
      setTimeout(() => {
        hideLoader();
        if (onProceed) {
          onProceed();
        } else {
          navigate("/proceed-to-payment-gateway");
        }
      }, 2000);
    }
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Top section */}
      {showTopSection && (
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center logo-container">
            <Logo name="CMSLogo" size="large" />
          </div>
          <div className="flex items-center">
            <Avatar
              size={32}
              src={userInfo?.profileImage}
              className="mr-2"
              alt={userFirstName || "User"}
            />
            <span className="text-bold text-primary">
              Hello, {userFirstName}
            </span>
          </div>
        </div>
      )}

      {/* Middle section */}
      <div className="flex flex-col items-center w-full px-4 mt-4 mb-8 max-w-[800px] mx-auto">
        <div className="flex justify-center mb-6">
          <WebIcon icon="Cup" className="w-[100px] h-[100px]" />
        </div>

        <h1 className="mb-2 text-center text-black heading-large">
          Unlock Premium
        </h1>
        <p className="mb-6 text-center text-regular text-darkGrey">
          NO COMMITTMENT, CANCEL ANYTIME.
        </p>

        <div className="w-full mb-6">
          {benefits.map((benefit, index) => (
            <div key={index} className="flex items-center mb-2">
              <CheckOutlined className="p-1 mr-2 text-black border-2 border-black rounded-full" />
              <span className="text-regular">{benefit}</span>
            </div>
          ))}
        </div>

        <div className="flex flex-col w-full gap-4">
          <div className="relative">
            <div className="absolute px-2 py-1 text-white rounded -top-3 right-2 bg-primary text-small">
              BEST VALUE
            </div>
            <button
              onClick={() => setSelectedPlan("yearly")}
              className="flex items-center w-full px-4 py-3 bg-white border rounded-md border-primary">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center mr-4 ${
                  selectedPlan === "yearly"
                    ? "bg-primary"
                    : "bg-white border-2 border-white"
                }`}>
                {selectedPlan === "yearly" && (
                  <CheckOutlined style={{ color: "white", fontSize: "16px" }} />
                )}
              </div>
              <div className="flex flex-col items-start">
                <span className="text-bold-x text-primary">
                  Free for 7 Days
                </span>
                <span className="text-black text-small">
                  &#x20A6;5000/Month after 7-days trial expires.
                </span>
              </div>
            </button>
          </div>
          <button
            onClick={() => setSelectedPlan("monthly")}
            className="flex items-center w-full px-4 py-3 text-white border rounded-md border-primary bg-primary">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center mr-4 ${
                selectedPlan === "monthly"
                  ? "bg-white"
                  : "bg-primary border-2 border-white"
              }`}>
              {selectedPlan === "monthly" && (
                <CheckOutlined
                  style={{
                    color: "var(--veegil-color-primary)",
                    fontSize: "16px",
                  }}
                />
              )}
            </div>
            <div className="flex flex-col items-start">
              <span className="text-bold-x">12 Months</span>
              <span className="text-small">&#x20A6;3000/Month</span>
            </div>
          </button>
        </div>

        <div className="w-full mt-6">
          <Button
            text="Proceed"
            icon={<ArrowRightOutlined />}
            onClick={handleProceed}
            isActive={!!selectedPlan}
            color="primary"
            className={`w-full mt-6 ${
              selectedPlan
                ? "bg-primary text-white"
                : "bg-darkBlue text-primary"
            }`}
          />
        </div>
      </div>

      {/* Footer */}
      {showFooter && (
        <div className="mt-auto">
          <Copyright />
        </div>
      )}
    </div>
  );
};

Subscription.propTypes = {
  showTopSection: PropTypes.bool,
  showFooter: PropTypes.bool,
  onProceed: PropTypes.func,
  selectedPlan: PropTypes.string,
  setSelectedPlan: PropTypes.func,
};

export default Subscription;
